import { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    X, ChevronLeft, ChevronRight, ChevronDown, Save, Database, Table,
    Link2, Calculator, CheckCircle, Loader2, AlertCircle,
    Plus, Wand2, Edit, Trash2, Calendar, Maximize, Minimize, Check, Search, Eye, Shield, ArrowRight, ArrowLeftRight,
    Server, Globe, Cloud, Smartphone, UploadCloud, Info, SlidersHorizontal
} from 'lucide-react';
import { DateTableWizard } from './DateTableWizard';
import { RelationshipReviewDialog } from './RelationshipReviewDialog';
import { useNavigate } from 'react-router-dom';
import { DataSourceWizard } from '../datasources/DataSourceWizard';

import ReactFlow, {
    addEdge,
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    MarkerType,
    ReactFlowProvider,
    useReactFlow,
    type Connection,
    type Edge,
    type Node,
    type ReactFlowInstance
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../ui/dialog';
import { useAuth } from '@/context/useAuth';
import { useNotification } from '@/context/NotificationContext';
import { apiGet, apiPost, apiPut, interactiveFetch } from '@/api';
import TableNode from './TableNode';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';
import { DataPreviewModal } from '@/components/common/DataPreviewModal';

// --- Types ---
interface Datasource {
    id: string;
    name: string;
    type: number;
    typeLabel?: string;
    connectionString?: string | null;
    configurationJson?: Record<string, any> | null;
    workspaceId?: string | null;
    gatewayId?: string | null;
}

interface Column {
    name: string;
    data_type: 'text' | 'number' | 'date' | 'boolean' | 'json';
    nullable?: boolean;
    raw_type?: string;
    synonyms?: string;
    format?: string;
    is_pd?: boolean;  // PHI/PII flag â€” relay agent will sanitize this column
}

interface TableInfo {
    name: string;
    columns: Column[];
    row_count?: number;
}

interface SelectedTable {
    name: string;
    source_name: string;
    columns: Column[];
}

interface Relationship {
    from_table: string;
    from_column: string;
    to_table: string;
    to_column: string;
    cardinality: 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many';
    crossFilteringBehavior?: string;
}

interface Measure {
    name: string;
    expression: string;
    format?: string;
    description?: string;
}

interface SemanticDomain {
    id: string;
    name: string;
    description?: string;
}

// --- Node Types for ReactFlow ---
const nodeTypes = {
    table: TableNode,
};

// ── Datasource type → icon + label (matches DataSourceWizard.tsx gallery) ──
const DS_TYPE_INFO: Record<number, { label: string; icon: any }> = {
    0:  { label: 'PostgreSQL',   icon: Database },
    1:  { label: 'MySQL',        icon: Database },
    2:  { label: 'SQL Server',   icon: Server },
    3:  { label: 'Oracle',       icon: Database },
    4:  { label: 'Snowflake',    icon: Globe },
    5:  { label: 'BigQuery',     icon: Cloud },
    6:  { label: 'Redshift',     icon: Globe },
    7:  { label: 'Azure SQL',    icon: Globe },
    8:  { label: 'Synapse',      icon: Globe },
    9:  { label: 'CSV',          icon: UploadCloud },
    10: { label: 'Excel',        icon: UploadCloud },
    11: { label: 'JSON',         icon: UploadCloud },
    12: { label: 'Parquet',      icon: UploadCloud },
    13: { label: 'DuckDB',       icon: Database },
    14: { label: 'RestAPI',      icon: Globe },
    15: { label: 'MCP Server',   icon: Smartphone },
    16: { label: 'Azure Blob',   icon: Cloud },
    17: { label: 'Salesforce',   icon: Cloud },
    18: { label: 'AWS S3',       icon: Cloud },
};

// --- Form Schema ---NS
const formSchema = z.object({
    name: z.string().min(2, 'Name is required'),
    description: z.string().optional(),
    datasource_id: z.string().min(1, 'Datasource is required'),
    domain_id: z.string().optional().or(z.literal('')),
    storage_mode: z.enum(['direct_query', 'import']),
    data_layer: z.enum(['bronze', 'silver', 'gold']),
});

type FormValues = z.infer<typeof formSchema>;

interface DataModelWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    dataModelId?: string | null;
}

export const DataModelWizard = ({ isOpen, onClose, onSuccess, dataModelId }: DataModelWizardProps) => {
    const [step, setStep] = useState<'CONNECT' | 'SELECT' | 'MODEL' | 'PROTECT' | 'REVIEW'>('CONNECT');
    const [datasources, setDatasources] = useState<Datasource[]>([]);
    const [availableTables, setAvailableTables] = useState<TableInfo[]>([]);
    const [selectedTables, setSelectedTables] = useState<SelectedTable[]>([]);
    const [relationships, setRelationships] = useState<Relationship[]>([]);
    const [measures, setMeasures] = useState<Measure[]>([]);
    const [domains, setDomains] = useState<SemanticDomain[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isIntrospecting, setIsIntrospecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ReactFlow state
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedColumn, setSelectedColumn] = useState<{ tableName: string, colName: string } | null>(null);

    // Relationship Edit State
    const [editingRelationship, setEditingRelationship] = useState<Relationship | null>(null);
    const [editingRelationshipIndex, setEditingRelationshipIndex] = useState<number | null>(null);
    const [isRelationshipDialogOpen, setIsRelationshipDialogOpen] = useState(false);
    const [reviewModal, setReviewModal] = useState<'tables' | 'measures' | 'relationships' | 'protected' | null>(null);
    const [expandedReviewTables, setExpandedReviewTables] = useState<string[]>([]);

    // Measure Dialog state
    const [isMeasureDialogOpen, setIsMeasureDialogOpen] = useState(false);
    const [editingMeasureIndex, setEditingMeasureIndex] = useState<number | null>(null);
    const [measureForm, setMeasureForm] = useState<Measure>({ name: '', expression: '', format: '', description: '' });
    const [loadingAI, setLoadingAI] = useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionIndex, setSuggestionIndex] = useState(0);
    const [cursorPos, setCursorPos] = useState(0);

    // AI Review State
    const [aiSuggestions, setAiSuggestions] = useState<Relationship[]>([]);
    const [isAiReviewOpen, setIsAiReviewOpen] = useState(false);

    // Date Table Wizard state
    const [isDateWizardOpen, setIsDateWizardOpen] = useState(false); // Added state
    const [isSourceWizardOpen, setIsSourceWizardOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false); // Full-screen state
    const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);

    // Preview State
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewTable, setPreviewTable] = useState('');

    // Persisted layout positions â€” populated from API on load, updated on every node drag
    const layoutPositionsRef = useRef<Record<string, { x: number; y: number }>>({});

    // PD Applier state â€” maps HIPAA identifiers to columns + actions
    const [pdApplierConfig, setPdApplierConfig] = useState<Record<string, { columns: string[]; action: string }>>({}); 
    const [selectedIdentifier, setSelectedIdentifier] = useState<string>('NAME');
    const [pdColumnSearch, setPdColumnSearch] = useState('');
    const [collapsedPdTables, setCollapsedPdTables] = useState<Set<string>>(new Set());
    // Custom identifiers â€” user-defined beyond the 19 HIPAA ones
    const [customIdentifiers, setCustomIdentifiers] = useState<Array<{ key: string; label: string }>>([]);
    const [addingCustomId, setAddingCustomId] = useState(false);
    const [customIdInput, setCustomIdInput] = useState('');
    // Entity key columns per table â€” for PD Registry stable tokenization
    const [pdEntityKeys, setPdEntityKeys] = useState<Record<string, string>>({});
    // Per-column mask options: fqcol -> { mask_char, mask_mode }
    const [maskOptions, setMaskOptions] = useState<Record<string, { mask_char: string; mask_mode: string }>>({});
    // Relay agent selector for the PROTECT step
    const [relayAgents, setRelayAgents] = useState<{ id: string; name: string; status: string }[]>([]);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');

    const { token } = useAuth();
    const { formatDate } = useTimezoneFormatter();
    const navigate = useNavigate();

    const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            storage_mode: 'import',
            data_layer: 'silver',
            domain_id: '',
        }
    });

    const selectedDatasourceId = watch('datasource_id');
    const [tableSearch, setTableSearch] = useState('');

    // Fix edge IDs to be deterministic for matching
    useEffect(() => {
        const newEdges: Edge[] = relationships.map((rel, i) => ({
            id: `e-${rel.from_table}-${rel.from_column}-${rel.to_table}-${rel.to_column}`,
            source: rel.from_table,
            sourceHandle: `${rel.from_column}-source`,
            target: rel.to_table,
            targetHandle: `${rel.to_column}-target`,
            animated: true,
            label: showSuggestions ? '' : rel.cardinality, // Hide label when suggestions open to reduce clutter? No.
            style: { stroke: '#2563eb', strokeWidth: 2, cursor: 'pointer' },
            markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#2563eb',
            },
            interactionWidth: 20 // Easier to click
        }));
        setEdges(newEdges);
    }, [relationships, setEdges]); // Removed showSuggestions dependency to avoid edge re-renders

    useEffect(() => {
        if (isOpen) {
            fetchDatasources();
            fetchDomains();
            // Fetch active relay agents for the PROTECT step selector
            apiGet('/relay/agents', token).then((data: any) => {
                if (Array.isArray(data)) setRelayAgents(data);
            }).catch(() => {});
            if (dataModelId) {
                loadDataModel(dataModelId);
            } else {
                resetWizard();
            }
        }
    }, [isOpen, dataModelId]);

    const resetWizard = () => {
        setStep('CONNECT');
        setAvailableTables([]);
        setSelectedTables([]);
        setRelationships([]);
        setMeasures([]);
        setError(null);
        setSuggestions([]);
        setShowSuggestions(false);
        setSuggestionIndex(0);
        setCursorPos(0);
        setPdApplierConfig({});
        setSelectedIdentifier('NAME');
        setPdColumnSearch('');
    };

    // --- Measure Intellisense Logic ---
    const handleExpressionChange = (val: string, cursor: number) => {
        setMeasureForm({ ...measureForm, expression: val });
        setCursorPos(cursor);

        const textBeforeCursor = val.slice(0, cursor);
        const lastBracketIndex = textBeforeCursor.lastIndexOf('[');
        const lastSpaceIndex = textBeforeCursor.lastIndexOf(' ');
        const lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');
        const lastFunctionIndex = Math.max(
            textBeforeCursor.lastIndexOf('SUM'),
            textBeforeCursor.lastIndexOf('AVG'),
            textBeforeCursor.lastIndexOf('COUNT'),
            textBeforeCursor.lastIndexOf('MIN'),
            textBeforeCursor.lastIndexOf('MAX')
        );

        if (lastBracketIndex !== -1 && lastBracketIndex > Math.max(lastSpaceIndex, lastNewlineIndex)) {
            const query = textBeforeCursor.slice(lastBracketIndex + 1).toLowerCase();
            const allColumns = selectedTables.flatMap(t => t.columns.map(c => `${t.name}.${c.name}`));
            const filtered = allColumns.filter(c => c.toLowerCase().includes(query));

            if (filtered.length > 0) {
                setSuggestions(filtered);
                setShowSuggestions(true);
                setSuggestionIndex(0);
            } else {
                setShowSuggestions(false);
            }
        } else {
            // Suggest functions if we just typed a letter and not inside a bracket
            const lastWord = textBeforeCursor.split(/[\s\n(]/).pop() || '';
            const functions = [
                'SUM(', 'AVG(', 'COUNT(', 'MIN(', 'MAX(', 'COUNT(DISTINCT ',
                'CALCULATE(', 'FILTER(', 'RELATED(', 'DISTINCTCOUNT(', 'SUMX(', 'AVERAGEX(',
                'YEAR(', 'MONTH(', 'DAY(', 'DATE(', 'IF(', 'SWITCH(', 'COALESCE('
            ];

            // Trigger on simple typing if length >= 1 OR if explicit empty trigger (Ctrl+Space passed as logic elsewhere)
            if (lastWord.length >= 1) {
                const filteredFuncs = functions.filter(f => f.toLowerCase().startsWith(lastWord.toLowerCase()));
                if (filteredFuncs.length > 0) {
                    setSuggestions(filteredFuncs);
                    setShowSuggestions(true);
                    setSuggestionIndex(0);
                } else {
                    setShowSuggestions(false);
                }
            } else {
                setShowSuggestions(false);
            }
        }
    };

    const triggerIntellisense = () => {
        handleExpressionChange(measureForm.expression, cursorPos);
        // Force show all functions if nothing typed? 
        // For now, let's just re-run logic. 
        // Actually, for Ctrl+Space with empty text, we want to show ALL functions/columns.
        const textBeforeCursor = measureForm.expression.slice(0, cursorPos);
        const lastWord = textBeforeCursor.split(/[\s\n(]/).pop() || '';
        if (lastWord.length === 0) {
            const allColumns = selectedTables.flatMap(t => t.columns.map(c => `[${t.name}.${c.name}]`)); // Add brackets for easier insertion
            const functions = [
                'SUM(', 'AVG(', 'COUNT(', 'MIN(', 'MAX(', 'COUNT(DISTINCT ',
                'CALCULATE(', 'FILTER(', 'RELATED(',
            ];
            setSuggestions([...functions, ...allColumns].slice(0, 50)); // Limit to 50
            setShowSuggestions(true);
        }
    };

    const applySuggestion = (suggestion: string) => {
        const val = measureForm.expression;
        const textBeforeCursor = val.slice(0, cursorPos);
        const textAfterCursor = val.slice(cursorPos);

        let newVal = '';
        let newCursorPos = 0;

        if (suggestion.includes('.')) {
            // It's a column [Table.Column]
            const lastBracketIndex = textBeforeCursor.lastIndexOf('[');
            newVal = textBeforeCursor.slice(0, lastBracketIndex + 1) + suggestion + ']' + textAfterCursor;
            newCursorPos = lastBracketIndex + suggestion.length + 2;
        } else {
            // It's a function
            const lastWordIndex = Math.max(
                textBeforeCursor.lastIndexOf(' '),
                textBeforeCursor.lastIndexOf('\n'),
                textBeforeCursor.lastIndexOf('('),
                -1
            );
            newVal = textBeforeCursor.slice(0, lastWordIndex + 1) + suggestion + textAfterCursor;
            newCursorPos = lastWordIndex + 1 + suggestion.length;
        }

        setMeasureForm({ ...measureForm, expression: newVal });
        setShowSuggestions(false);
        // We'd ideally refocus the textarea here, but for now we rely on the next interaction
    };

    const fetchDatasources = async () => {
        try {
            const data = await apiGet('/datasources', token);
            setDatasources(data || []);
        } catch (e) {
            console.error('Failed to fetch datasources', e);
        }
    };

    const fetchDomains = async () => {
        try {
            const response = await apiGet('/semantic-kb/domains', token);
            // Response format for list might be { domains: [], total: ... } or just []
            setDomains(response.domains || response || []);
        } catch (e) {
            console.error('Failed to fetch domains', e);
        }
    };

    // State for locally created model (when saving draft)
    const [createdModelId, setCreatedModelId] = useState<string | null>(null);
    const effectiveModelId = dataModelId || createdModelId;

    const loadDataModel = async (id: string) => {
        setIsLoading(true);
        try {
            const data = await apiGet(`/datamodels/${id}`, token);
            // console.log('[[DEBUG]] Loaded Data Model:', JSON.stringify(data, null, 2));
            if (data) {
                setValue('name', data.name);
                setValue('description', data.description);
                setValue('datasource_id', data.datasource_id);
                setValue('domain_id', data.domain_id || '');
                setValue('storage_mode', data.storage_mode);
                setValue('data_layer', data.data_layer);

                // Restore saved layout positions
                const savedPositions: Record<string, { x: number; y: number }> =
                    data.layout_json?.positions || {};
                layoutPositionsRef.current = savedPositions;

                setSelectedTables(data.tables || []);
                setRelationships(data.relationships || []);
                setMeasures(data.measures || []);
                // Restore PD Applier config
                setPdApplierConfig(data.pd_applier_config?.mappings || {});
                // Restore custom identifiers
                if (data.pd_applier_config?.custom_identifiers) {
                    setCustomIdentifiers(data.pd_applier_config.custom_identifiers);
                }
                // Restore entity key columns
                if (data.pd_applier_config?.entity_keys) {
                    setPdEntityKeys(data.pd_applier_config.entity_keys);
                }
                // Restore pinned relay agent
                if (data.relay_agent_id) {
                    setSelectedAgentId(data.relay_agent_id);
                }
                setStep('CONNECT');
            }
        } catch (e) {
            console.error('Failed to load data model', e);
            setError('Failed to load data model details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleIntrospect = async () => {
        if (!selectedDatasourceId) {
            setError('Please select a datasource first');
            return;
        }

        setIsIntrospecting(true);
        setError(null);
        try {
            // File datasources (CSV=9, Excel=10, JSON=11, Parquet=12) are introspected
            // directly via the interactive runner (8001) which has openpyxl/pandas.
            // This keeps the control plane thin.
            const FILE_TYPE_VALUES = [9, 10, 11, 12];
            const selectedDs = datasources.find((d: Datasource) => d.id === selectedDatasourceId);
            const isFileDatasource = selectedDs && FILE_TYPE_VALUES.includes(Number(selectedDs.type));
            const isEtlWorkspace = isFileDatasource && selectedDs.configurationJson?.source === 'etl_workspace';

            if (isEtlWorkspace) {
                if (!selectedDs.connectionString) {
                    throw new Error('ETL Workspace has no S3 URI.');
                }
                const result = await interactiveFetch('/datasource/etl-schema', token, {
                    method: 'POST',
                    body: JSON.stringify({
                        s3_uri: selectedDs.connectionString,
                    }),
                });
                const formattedTables = (result.tables || []).map((t: any) => ({
                    name: t.name,
                    columns: t.columns.map((c: any) => ({
                        name: c.name,
                        column_name: c.name,
                        data_type: c.dataType || c.data_type || 'text',
                        nullable: c.nullable !== false,
                    }))
                }));
                setAvailableTables(formattedTables);
            } else if (isFileDatasource) {
                if (!selectedDs.connectionString) {
                    throw new Error('File datasource has no S3 URI. Please re-upload the file.');
                }
                const config = selectedDs.configurationJson || {};
                const fileName = config.fileName || selectedDs.connectionString.split('/').pop() || '';
                const result = await interactiveFetch('/datasource/file-schema', token, {
                    method: 'POST',
                    body: JSON.stringify({
                        s3_uri: selectedDs.connectionString,
                        file_name: fileName,
                    }),
                });
                const columns = (result.columns || []).map((col: string) => ({
                    name: col,
                    column_name: col,
                    data_type: 'text',
                    nullable: true,
                }));
                setAvailableTables([{
                    name: fileName.replace(/\.[^/.]+$/, ''),
                    columns,
                }]);
            } else {
                // Database datasource — use control plane introspection as normal
                const result = await apiPost('/datamodels/introspect', token, {
                    datasource_id: selectedDatasourceId
                });
                setAvailableTables(result.tables || []);
            }
            setStep('SELECT');
        } catch (e: any) {
            setError(e.message || 'Failed to introspect datasource');
        } finally {
            setIsIntrospecting(false);
        }
    };

    const handleTableToggle = (table: TableInfo) => {
        const exists = selectedTables.find(t => t.name === table.name);
        if (exists) {
            // Remove table
            setSelectedTables(selectedTables.filter(t => t.name !== table.name));
            // Remove associated relationships
            setRelationships(relationships.filter(r => r.from_table !== table.name && r.to_table !== table.name));
        } else {
            setSelectedTables([...selectedTables, {
                name: table.name,
                source_name: table.name,
                columns: table.columns
            }]);
        }
    };

    const handleAddRelationship = () => {
        if (selectedTables.length < 2) {
            setError('You need at least 2 tables to create a relationship');
            return;
        }
        setRelationships([...relationships, {
            from_table: selectedTables[0].name,
            from_column: selectedTables[0].columns[0]?.name || '',
            to_table: selectedTables[1].name,
            to_column: selectedTables[1].columns[0]?.name || '',
            cardinality: 'many_to_one'
        }]);
    };

    const handleRemoveRelationship = (index: number) => {
        setRelationships(relationships.filter((_, i) => i !== index));
    };

    // Sync Nodes with Selected Tables (preserving positions)
    useEffect(() => {
        setNodes((prevNodes) => {
            // Create a map of existing nodes for quick lookup of positions
            const existingNodesMap = new Map(prevNodes.map(n => [n.id, n]));

            return selectedTables.map((table, index) => {
                const existingNode = existingNodesMap.get(table.name);
                // Priority: 1) current dragged position, 2) saved layout from API, 3) default grid
                const savedPos = layoutPositionsRef.current[table.name];
                const position = existingNode?.position
                    ?? savedPos
                    ?? { x: 250 * (index % 3) + 50, y: 200 * Math.floor(index / 3) + 50 };

                return {
                    id: table.name,
                    type: 'table',
                    position,
                    data: {
                        label: table.name,
                        columns: table.columns.map((c: any) => ({
                            name: c.name,
                            type: c.data_type,
                            isPrimaryKey: c.name.toLowerCase().includes('id'),
                            isForeignKey: false,
                            synonyms: c.synonyms,
                            isPd: !!c.is_pd,
                        })),
                        onColumnClick: (colName: string) => {
                            setSelectedColumn({ tableName: table.name, colName });
                        },
                        onPhiToggle: (colName: string) => {
                            setSelectedTables(prev => prev.map(t => {
                                if (t.name !== table.name) return t;
                                return {
                                    ...t,
                                    columns: t.columns.map((c: any) => {
                                        if (c.name !== colName) return c;
                                        return { ...c, is_pd: !c.is_pd };
                                    })
                                };
                            }));
                        },
                    },
                };
            });
        });
    }, [selectedTables, setNodes]);

    // Auto-purge pdApplierConfig entries whose columns are no longer marked is_pd.
    // This fires whenever selectedTables changes (e.g. the user toggles is_pd off in Step 3).
    useEffect(() => {
        const validPdCols = new Set<string>();
        selectedTables.forEach(t =>
            t.columns.forEach((c: any) => {
                if (c.is_pd) validPdCols.add(`${t.name}.${c.name}`);
            })
        );

        setPdApplierConfig(prev => {
            let changed = false;
            const cleaned: typeof prev = {};
            Object.entries(prev).forEach(([key, conf]) => {
                const validCols = (conf.columns || []).filter((c: string) => validPdCols.has(c));
                if (validCols.length > 0) {
                    cleaned[key] = { ...conf, columns: validCols };
                } else {
                    changed = true; // at least one entry was dropped
                }
                if (validCols.length !== (conf.columns || []).length) changed = true;
            });
            return changed ? cleaned : prev; // avoid unnecessary re-renders
        });
    }, [selectedTables]);

    // Handle Deletion from Graph (Delete key / Backspace)
    const onNodesDelete = useCallback(
        (deleted: Node[]) => {
            const deletedTableNames = new Set(deleted.map((node) => node.id));

            // Remove from selectedTables
            setSelectedTables((prev) => prev.filter((t) => !deletedTableNames.has(t.name)));

            // Remove associated relationships
            setRelationships((prev) => prev.filter(
                (r) => !deletedTableNames.has(r.from_table) && !deletedTableNames.has(r.to_table)
            ));
        },
        []
    );

    // Initialize Edges from Relationships - MOVED TO ABOVE useEffect to fix dependency issues and add interactionWidth
    // Kept this comment to indicate removal of previous block
    useEffect(() => {
        // Redundant block removed, logic moved up
    }, []);

    const handleAutoLayout = useCallback(() => {
        const dagreGraph = new dagre.graphlib.Graph();
        dagreGraph.setDefaultEdgeLabel(() => ({}));

        const nodeWidth = 280;
        const nodeHeight = 200;

        dagreGraph.setGraph({ rankdir: 'LR' });

        nodes.forEach((node) => {
            dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
        });

        edges.forEach((edge) => {
            dagreGraph.setEdge(edge.source, edge.target);
        });

        dagre.layout(dagreGraph);

        const layoutedNodes = nodes.map((node) => {
            const nodeWithPosition = dagreGraph.node(node.id);
            return {
                ...node,
                position: {
                    x: nodeWithPosition.x - nodeWidth / 2,
                    y: nodeWithPosition.y - nodeHeight / 2,
                },
            };
        });

        // Persist auto-layout positions to the ref so they survive step changes
        const newPositions: Record<string, { x: number; y: number }> = {};
        layoutedNodes.forEach(n => { newPositions[n.id] = n.position; });
        layoutPositionsRef.current = newPositions;

        setNodes(layoutedNodes);

        setTimeout(() => {
            rfInstance?.fitView({ padding: 0.2 });
        }, 50);
    }, [nodes, edges, rfInstance, setNodes]);

    const onConnect = useCallback(
        (params: Connection) => {
            const newRel: Relationship = {
                from_table: params.source!,
                from_column: params.sourceHandle!.replace('-source', ''),
                to_table: params.target!,
                to_column: params.targetHandle!.replace('-target', ''),
                cardinality: 'many_to_one',
                crossFilteringBehavior: 'Single'
            };
            setRelationships([...relationships, newRel]);
        },
        [relationships]
    );

    const handleSuggestRelationships = async () => {
        setLoadingAI(true);
        try {
            const res = await apiPost('/datamodels/suggest-relationships', token, selectedTables);
            // Filter out existing relationships from suggestions
            const uniqueSuggestions = res.filter((s: Relationship) => {
                return !relationships.some(r =>
                    r.from_table === s.from_table &&
                    r.from_column === s.from_column &&
                    r.to_table === s.to_table &&
                    r.to_column === s.to_column
                );
            });

            if (uniqueSuggestions.length > 0) {
                setAiSuggestions(uniqueSuggestions);
                setIsAiReviewOpen(true);
            } else {
                // optional: notify user no new suggestions found
                setError("No new relationships found.");
                setTimeout(() => setError(null), 3000);
            }
        } catch (err) {
            console.error("Failed to suggest relationships", err);
            setError("Failed to get AI suggestions.");
        } finally {
            setLoadingAI(false);
        }
    };

    const handleApplyAiSuggestions = (selected: Relationship[]) => {
        setRelationships([...relationships, ...selected]);
        setAiSuggestions([]);
        setIsAiReviewOpen(false);
    };

    const handleSaveMeasure = () => {
        if (!measureForm.name || !measureForm.expression) {
            setError("Name and Expression are required for the measure.");
            return;
        }

        const newMeasures = [...measures];
        if (editingMeasureIndex !== null) {
            newMeasures[editingMeasureIndex] = measureForm;
        } else {
            newMeasures.push(measureForm);
        }
        setMeasures(newMeasures);
        setIsMeasureDialogOpen(false);
        setMeasureForm({ name: '', expression: '', format: '', description: '' });
        setEditingMeasureIndex(null);
    };

    const handleEditMeasure = (index: number) => {
        setMeasureForm(measures[index]);
        setEditingMeasureIndex(index);
        setIsMeasureDialogOpen(true);
    };

    const handleDeleteMeasure = (index: number) => {
        const newMeasures = [...measures];
        newMeasures.splice(index, 1);
        setMeasures(newMeasures);
    };

    const handleEdgeClick = (event: React.MouseEvent, edge: Edge) => {
        event.stopPropagation();
        // Find relationship index
        const index = relationships.findIndex(r =>
            `${r.from_table}-${r.from_column}-${r.to_table}-${r.to_column}` === edge.id.replace('e', '').substring(edge.id.indexOf('-') + 1) ||
            edge.id.includes(`${r.from_table}-${r.from_column}-${r.to_table}-${r.to_column}`) // Fallback matching
        );

        if (index !== -1) {
            setEditingRelationship(relationships[index]);
            setEditingRelationshipIndex(index);
            setIsRelationshipDialogOpen(true);
        }
    };

    const handleSaveRelationship = () => {
        if (editingRelationshipIndex !== null && editingRelationship) {
            const newRels = [...relationships];
            newRels[editingRelationshipIndex] = editingRelationship;
            setRelationships(newRels);
            setIsRelationshipDialogOpen(false);
            setEditingRelationship(null);
            setEditingRelationshipIndex(null);
        }
    };

    const handleDeleteRelationship = () => {
        if (editingRelationshipIndex !== null) {
            const newRels = [...relationships];
            newRels.splice(editingRelationshipIndex, 1);
            setRelationships(newRels);
            setIsRelationshipDialogOpen(false);
            setEditingRelationship(null);
            setEditingRelationshipIndex(null);
        }
    };

    const handleSaveDraft = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = watch();

            // Ensure required fields for Draft
            const draftName = data.name || `Draft Model - ${formatDate(dayjs())}`;
            const draftDatasourceId = data.datasource_id || selectedDatasourceId;

            if (!draftDatasourceId) {
                throw new Error("Datasource must be selected before adding a Date Table.");
            }

            // Capture current node positions
            const positions: Record<string, { x: number; y: number }> = {};
            nodes.forEach(n => { positions[n.id] = n.position; });
            // Merge with any previously saved positions (covers tables not yet on canvas)
            const mergedPositions = { ...layoutPositionsRef.current, ...positions };
            layoutPositionsRef.current = mergedPositions;

            // Clean up pdApplierConfig to only include columns that are still marked as is_pd
            const validPdColumns = new Set<string>();
            selectedTables.forEach(t => {
                t.columns.forEach((c: any) => {
                    if (c.is_pd) validPdColumns.add(`${t.name}.${c.name}`);
                });
            });

            const cleanedPdApplierConfig: any = {};
            Object.entries(pdApplierConfig).forEach(([key, conf]) => {
                const validCols = (conf.columns || []).filter((c: string) => validPdColumns.has(c));
                if (validCols.length > 0) {
                    cleanedPdApplierConfig[key] = { ...conf, columns: validCols };
                }
            });

            const payload: any = {
                ...data,
                name: draftName,
                datasource_id: draftDatasourceId,
                tables: selectedTables,
                relationships,
                measures,
                layout_json: { positions: mergedPositions },
                pd_applier_config: Object.keys(cleanedPdApplierConfig).length > 0
                    ? { version: 1, mappings: cleanedPdApplierConfig, ...(customIdentifiers.length > 0 ? { custom_identifiers: customIdentifiers } : {}), ...(Object.keys(pdEntityKeys).length > 0 ? { entity_keys: pdEntityKeys } : {}) } : null,
                relay_agent_id: selectedAgentId || undefined,
                status: 'draft'
            };

            if (payload.domain_id === '') {
                payload.domain_id = null;
            }

            // Sanitize relationships before saving (ensure both tables exist)
            const validRelationships = relationships.filter(r =>
                selectedTables.some(t => t.name === r.from_table) &&
                selectedTables.some(t => t.name === r.to_table)
            );
            payload.relationships = validRelationships;

            const response = await apiPost('/datamodels', token, payload);
            setCreatedModelId(response.id);
            setIsLoading(false);
            return response.id;
        } catch (e: any) {
            setError(e.message || 'Failed to save draft');
            setIsLoading(false);
            return null;
        }
    };

    const onSubmit = async (data: FormValues) => {
        setIsLoading(true);
        setError(null);
        try {
            // Capture current node positions
            const positions: Record<string, { x: number; y: number }> = {};
            nodes.forEach(n => { positions[n.id] = n.position; });
            const mergedPositions = { ...layoutPositionsRef.current, ...positions };
            layoutPositionsRef.current = mergedPositions;

            // Clean up pdApplierConfig to only include columns that are still marked as is_pd
            const validPdColumns = new Set<string>();
            selectedTables.forEach(t => {
                t.columns.forEach((c: any) => {
                    if (c.is_pd) validPdColumns.add(`${t.name}.${c.name}`);
                });
            });

            const cleanedPdApplierConfig: any = {};
            Object.entries(pdApplierConfig).forEach(([key, conf]) => {
                const validCols = (conf.columns || []).filter((c: string) => validPdColumns.has(c));
                if (validCols.length > 0) {
                    cleanedPdApplierConfig[key] = { ...conf, columns: validCols };
                }
            });

            const payload: any = {
                ...data,
                tables: selectedTables,
                relationships,
                measures,
                layout_json: { positions: mergedPositions },
                pd_applier_config: Object.keys(cleanedPdApplierConfig).length > 0
                    ? { version: 1, mappings: cleanedPdApplierConfig, ...(customIdentifiers.length > 0 ? { custom_identifiers: customIdentifiers } : {}), ...(Object.keys(pdEntityKeys).length > 0 ? { entity_keys: pdEntityKeys } : {}) } : null,
                relay_agent_id: selectedAgentId || undefined,
                status: 'draft'
            };

            if (payload.domain_id === '') {
                payload.domain_id = null;
            }

            // Sanitize relationships before saving
            const validRelationships = relationships.filter(r =>
                selectedTables.some(t => t.name === r.from_table) &&
                selectedTables.some(t => t.name === r.to_table)
            );
            payload.relationships = validRelationships;

            // console.log('[[DEBUG]] Saving Payload:', JSON.stringify(payload, null, 2));

            if (effectiveModelId) {
                // console.log('[[DEBUG]] Updating existing model:', effectiveModelId);
                await apiPut(`/datamodels/${effectiveModelId}`, token, payload);
            } else {
                // console.log('[[DEBUG]] Creating new model');
                const response = await apiPost('/datamodels', token, payload);
                // logic below assumes we close wizard.
                if (response && response.id) {
                    setCreatedModelId(response.id);
                }
            }

            onSuccess();
            onClose();
        } catch (e: any) {
            setError(e.message || 'Failed to save data model');
        } finally {
            setIsLoading(false);
        }
    };



    const steps = [
        { key: 'CONNECT', label: 'Connect', icon: Database },
        { key: 'SELECT', label: 'Select Tables', icon: Table },
        { key: 'MODEL', label: 'Model', icon: Link2 },
        { key: 'PROTECT', label: 'Protect Data', icon: Shield },
        { key: 'REVIEW', label: 'Review', icon: CheckCircle }
    ];

    const currentStepIndex = steps.findIndex(s => s.key === step);

    return (
        <div className={`flex flex-col bg-white dark:bg-slate-950 overflow-hidden ${isFullScreen ? 'fixed inset-0 z-50 h-screen w-screen' : 'h-full'}`}>
            {/* Minimal Header */}
            <div className="h-14 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between px-6 bg-white dark:bg-slate-950 shrink-0">
                <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                        {dataModelId ? 'Edit Data Model' : 'Create Data Model'}
                    </h2>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center gap-2">
                        {steps.map((s, idx) => (
                            <div key={s.key} className="flex items-center">
                                <span className={`text-sm font-medium ${s.key === step ? 'text-primary font-bold' : 'text-gray-400 dark:text-slate-500'}`}>
                                    {idx + 1}. {s.label}
                                </span>
                                {idx < steps.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300 mx-2" />}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => setIsFullScreen(!isFullScreen)} title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}>
                        {isFullScreen ? <Minimize size={18} /> : <Maximize size={18} />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={onClose}>
                        <X size={20} className="text-gray-500" />
                    </Button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto bg-gray-50/50 dark:bg-slate-900/50 p-6">
                <AnimatePresence mode="wait">
                    {/* STEP 1: CONNECT */}
                    {step === 'CONNECT' && (
                        <motion.div
                            key="connect"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="max-w-4xl mx-auto space-y-6"
                        >
                            <div className="flex items-center gap-3 mb-6 px-4 py-3 bg-blue-50/80 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                                <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Select a data source below to continue. Choose your database or file connection.</p>
                            </div>

                            {/* Datasource Grid - Compact */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {datasources.map(ds => {
                                    const isSelected = selectedDatasourceId === ds.id;
                                    return (
                                        <Card
                                            key={ds.id}
                                            onClick={() => setValue('datasource_id', ds.id)}
                                            className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500 group relative overflow-hidden ${isSelected
                                                ? 'border-blue-600 ring-2 ring-blue-100 dark:ring-blue-900 shadow-md bg-blue-50/50 dark:bg-blue-900/20'
                                                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                                                }`}
                                        >
                                            <div className="p-4 flex flex-col items-center text-center space-y-3">
                                                {(() => {
                                                    const typeInfo = DS_TYPE_INFO[Number(ds.type)];
                                                    const TypeIcon = typeInfo?.icon || Database;
                                                    return (
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200 ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500 group-hover:bg-blue-50 group-hover:text-blue-500'}`}>
                                                            <TypeIcon className="w-5 h-5" />
                                                        </div>
                                                    );
                                                })()}
                                                <div>
                                                    <h3 className="font-semibold text-sm">{ds.name}</h3>
                                                    <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]">
                                                        {DS_TYPE_INFO[Number(ds.type)]?.label || ds.typeLabel || 'Unknown'}
                                                    </p>
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <div className="absolute top-3 right-3 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white">
                                                    <CheckCircle className="w-4 h-4" />
                                                </div>
                                            )}
                                        </Card>
                                    )
                                })}

                                {/* Add New Source Card */}
                                <Card
                                    onClick={() => setIsSourceWizardOpen(true)}
                                    className="border-dashed border-2 border-gray-300 bg-gray-50/50 hover:bg-gray-50 hover:border-blue-400 cursor-pointer transition-all flex flex-col items-center justify-center p-6 min-h-[200px] group"
                                >
                                    <div className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                        <Plus className="w-6 h-6 text-gray-400 group-hover:text-blue-500" />
                                    </div>
                                    <span className="font-medium text-sm text-gray-600 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400">Add New Source</span>
                                </Card>
                            </div>

                            {error && (
                                <div className="p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 justify-center">
                                    <AlertCircle size={18} />
                                    {error}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* STEP 2: SELECT TABLES */}
                    {step === 'SELECT' && (
                        <motion.div
                            key="select"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-4 h-full flex flex-col"
                        >
                            <div>
                                <p className="text-sm text-gray-500">Choose tables to include in your data model.</p>
                            </div>

                            <div className="flex-1 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800 shadow-sm flex flex-col min-h-0">
                                <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex justify-between items-center shrink-0">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-medium text-gray-700 dark:text-slate-300">{availableTables.length} tables found</span>
                                        <div className="h-4 w-px bg-gray-300 dark:bg-slate-600" />
                                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">{selectedTables.length} selected</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2" />
                                            <input
                                                type="text"
                                                placeholder="Search tables..."
                                                className="w-48 text-sm pl-8 pr-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                                                value={tableSearch}
                                                onChange={(e) => setTableSearch(e.target.value)}
                                            />
                                            {tableSearch && (
                                                <button onClick={() => setTableSearch('')} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                if (selectedTables.length === availableTables.length) {
                                                    setSelectedTables([]);
                                                } else {
                                                    setSelectedTables(availableTables.map(t => ({
                                                        name: t.name,
                                                        source_name: t.name,
                                                        columns: t.columns
                                                    })));
                                                }
                                            }}
                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        >
                                            {selectedTables.length === availableTables.length ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {availableTables
                                            .filter(table => table.name.toLowerCase().includes(tableSearch.toLowerCase()))
                                            .map(table => {
                                                const isSelected = selectedTables.some(t => t.name === table.name);
                                                return (
                                                    <div
                                                        key={table.name}
                                                        className={`relative flex items-start p-4 rounded-lg border cursor-pointer transition-all group ${isSelected ? 'border-blue-500 bg-blue-50/30 dark:bg-blue-900/20 ring-1 ring-blue-500' : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-sm'
                                                            }`}
                                                        onClick={() => handleTableToggle(table)}
                                                    >
                                                        <div className="flex items-center h-5">
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onCheckedChange={() => handleTableToggle(table)}
                                                            />
                                                        </div>
                                                        <div className="ml-3 text-sm flex-1">
                                                            <div className="flex justify-between items-center group">
                                                                <label className={`font-medium ${isSelected ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-slate-200'} cursor-pointer`}>
                                                                    {table.name}
                                                                </label>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        setPreviewTable(table.name);
                                                                        setPreviewOpen(true);
                                                                    }}
                                                                    title="Preview Table Data"
                                                                >
                                                                    <Eye className="w-5 h-5" />
                                                                </Button>
                                                            </div>
                                                            <p className="text-gray-500 dark:text-slate-400 mt-1 text-xs">
                                                                {table.columns.length} columns
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* STEP 3: MODEL */}
                    {step === 'MODEL' && (
                        <motion.div
                            key="model"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-4 h-full flex flex-col"
                        >
                            <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg">
                                <p className="text-sm text-slate-600 dark:text-slate-400 flex-1">Define relationships, measures, and set synonyms for AI.</p>
                                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                                    <Shield className="w-3.5 h-3.5 text-amber-600" />
                                    <span className="text-[11px] text-amber-700 dark:text-amber-300 font-medium">Click columns → toggle Protected Data for sensitive fields</span>
                                </div>
                            </div>

                            {/* Main Canvas Area */}
                            <div className="w-full flex-1 min-h-[500px] border border-gray-200 dark:border-slate-800 rounded-lg bg-gray-50 dark:bg-slate-900 relative overflow-hidden">
                                <ReactFlowProvider>
                                    <ReactFlow
                                        nodes={nodes}
                                        edges={edges}
                                        onNodesChange={onNodesChange}
                                        onEdgesChange={onEdgesChange}
                                        onNodesDelete={onNodesDelete}
                                        onConnect={onConnect}
                                        onEdgeClick={handleEdgeClick}
                                        onInit={setRfInstance}
                                        onNodeDragStop={(_event, _node, allNodes) => {
                                            // Persist all current node positions whenever any node is dragged
                                            const updated: Record<string, { x: number; y: number }> = {};
                                            allNodes.forEach(n => { updated[n.id] = n.position; });
                                            layoutPositionsRef.current = {
                                                ...layoutPositionsRef.current,
                                                ...updated
                                            };
                                        }}
                                        nodeTypes={nodeTypes}
                                        fitView
                                        fitViewOptions={{ padding: 0.2 }}
                                        minZoom={0.1}
                                    >
                                        <Background />
                                        <Controls position="bottom-right" />
                                    </ReactFlow>
                                </ReactFlowProvider>

                                {/* Action Buttons (Top Right) */}
                                <div className="absolute top-4 right-4 flex gap-2 z-10">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm"
                                        onClick={handleAutoLayout}
                                    >
                                        <Wand2 className="w-4 h-4 mr-1.5" />
                                        Auto Layout
                                    </Button>
                                    {/* <Button
                                            variant="outline"
                                            size="sm"
                                            className="bg-white text-blue-700 border-blue-200 hover:bg-blue-50 transition-all font-semibold shadow-sm"
                                            onClick={async () => {
                                                if (effectiveModelId) {
                                                    setIsDateWizardOpen(true);
                                                } else {
                                                    const newId = await handleSaveDraft();
                                                    if (newId) setIsDateWizardOpen(true);
                                                }
                                            }}
                                        >
                                            <Calendar className="w-4 h-4 mr-1.5" />
                                            Add Date Table
                                        </Button> */}
                                    <Dialog open={isMeasureDialogOpen} onOpenChange={setIsMeasureDialogOpen}>
                                        <DialogTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 hover:border-emerald-300 transition-all font-semibold"
                                                onClick={() => {
                                                    setEditingMeasureIndex(null);
                                                    setMeasureForm({ name: '', expression: '', format: '', description: '' });
                                                }}
                                            >
                                                <Plus className="w-4 h-4 mr-1.5" />
                                                Add Measure
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="sm:max-w-[600px] bg-white border-slate-200 shadow-2xl rounded-2xl p-0 overflow-hidden">
                                            <DialogHeader className="p-6 bg-slate-50 border-b border-slate-100">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                                                        <Calculator size={20} />
                                                    </div>
                                                    <DialogTitle className="text-xl font-bold text-slate-900">
                                                        {editingMeasureIndex !== null ? 'Edit Measure' : 'Create New Measure'}
                                                    </DialogTitle>
                                                </div>
                                            </DialogHeader>

                                            <div className="p-8 space-y-6">
                                                <div className="space-y-2">
                                                    <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Measure Name</Label>
                                                    <Input
                                                        id="name"
                                                        value={measureForm.name}
                                                        onChange={(e) => setMeasureForm({ ...measureForm, name: e.target.value })}
                                                        placeholder="e.g. Total Revenue"
                                                        className="h-11 bg-slate-50 border-slate-200 focus:bg-white focus:ring-emerald-500 transition-all"
                                                    />
                                                </div>

                                                <div className="space-y-2 relative">
                                                    <div className="flex items-center justify-between">
                                                        <Label htmlFor="expression" className="text-sm font-semibold text-slate-700">Measure Expression</Label>
                                                        <span className="text-[10px] text-slate-400 font-mono uppercase">Calculated Field</span>
                                                    </div>
                                                    <Textarea
                                                        id="expression"
                                                        value={measureForm.expression}
                                                        onChange={(e) => handleExpressionChange(e.target.value, e.target.selectionStart)}
                                                        onKeyDown={(e) => {
                                                            if (e.ctrlKey && e.code === 'Space') {
                                                                e.preventDefault();
                                                                triggerIntellisense();
                                                                return;
                                                            }
                                                            if (showSuggestions) {
                                                                if (e.key === 'ArrowDown') {
                                                                    e.preventDefault();
                                                                    setSuggestionIndex(prev => (prev + 1) % suggestions.length);
                                                                } else if (e.key === 'ArrowUp') {
                                                                    e.preventDefault();
                                                                    setSuggestionIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
                                                                } else if (e.key === 'Enter' || e.key === 'Tab') {
                                                                    e.preventDefault();
                                                                    applySuggestion(suggestions[suggestionIndex]);
                                                                } else if (e.key === 'Escape') {
                                                                    setShowSuggestions(false);
                                                                }
                                                            }
                                                        }}
                                                        placeholder="SUM([Amount]) * 1.2"
                                                        className="font-mono text-sm h-32 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:bg-white dark:focus:bg-slate-950 focus:ring-emerald-500 transition-all resize-none dark:text-slate-200"
                                                    />

                                                    {showSuggestions && (
                                                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto overflow-x-hidden top-[100%]">
                                                            {suggestions.map((suggestion, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className={`px-4 py-2 text-sm cursor-pointer flex items-center gap-2 ${idx === suggestionIndex
                                                                        ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                                                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'
                                                                        }`}
                                                                    onClick={() => applySuggestion(suggestion)}
                                                                >
                                                                    <span className="text-slate-400 dark:text-slate-500">
                                                                        {suggestion.includes('.') || suggestion.startsWith('[') ? <Table size={14} /> : <Calculator size={14} />}
                                                                    </span>
                                                                    <span className="truncate">{suggestion}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <p className="text-[11px] text-slate-500 italic">Common patterns: SUM([col]), AVG([col]), COUNT([col])</p>
                                                </div>

                                                <div className="grid grid-cols-2 gap-6">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="format" className="text-sm font-semibold text-slate-700">Format String</Label>
                                                        <Input
                                                            id="format"
                                                            value={measureForm.format}
                                                            onChange={(e) => setMeasureForm({ ...measureForm, format: e.target.value })}
                                                            placeholder="$ #,##0.00"
                                                            className="h-11 bg-slate-50 border-slate-200 focus:bg-white focus:ring-emerald-500 transition-all"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="desc" className="text-sm font-semibold text-slate-700">Description</Label>
                                                        <Input
                                                            id="desc"
                                                            value={measureForm.description}
                                                            onChange={(e) => setMeasureForm({ ...measureForm, description: e.target.value })}
                                                            placeholder="Optional context..."
                                                            className="h-11 bg-slate-50 border-slate-200 focus:bg-white focus:ring-emerald-500 transition-all"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <DialogFooter className="p-6 bg-slate-50 border-t border-slate-100">
                                                <Button variant="ghost" onClick={() => setIsMeasureDialogOpen(false)} className="text-slate-500 hover:text-slate-700">
                                                    Cancel
                                                </Button>
                                                <Button
                                                    onClick={handleSaveMeasure}
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-200/50 px-8 transition-all hover:-translate-y-0.5"
                                                >
                                                    {editingMeasureIndex !== null ? 'Update Measure' : 'Create Measure'}
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSuggestRelationships}
                                        disabled={loadingAI}
                                        className="bg-white dark:bg-slate-800 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                                    >
                                        {loadingAI ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                                        Suggest Relationships (AI)
                                    </Button>
                                </div>

                                {/* Measures List Overlay (Bottom Left) */}
                                {measures.length > 0 && (
                                    <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur shadow-xl border border-slate-200 rounded-xl p-5 w-72 max-h-[250px] overflow-y-auto z-10 transition-all animate-in fade-in slide-in-from-bottom-2">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                            <h4 className="font-bold text-[11px] text-slate-500 uppercase tracking-wider">Calculated Measures</h4>
                                        </div>
                                        <div className="space-y-1.5">
                                            {measures.map((m, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-sm group p-2 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                                                    <div className="flex flex-col min-w-0 pr-2">
                                                        <span className="font-semibold text-slate-800 truncate" title={m.name}>{m.name}</span>
                                                        <span className="text-[10px] text-slate-400 font-mono truncate">{m.expression}</span>
                                                    </div>
                                                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-emerald-50 hover:text-emerald-600" onClick={() => handleEditMeasure(idx)}>
                                                            <Edit className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-red-50 hover:text-red-600" onClick={() => handleDeleteMeasure(idx)}>
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Property Panel (Right Side - Slide Over) */}
                                {selectedColumn && (
                                    <div className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-slate-900 shadow-xl z-20 border-l border-gray-200 dark:border-slate-800 flex flex-col">
                                        <div className="p-4 border-b border-gray-200 dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-800">
                                            <h4 className="font-semibold text-gray-900 dark:text-slate-100">Column Properties</h4>
                                            <Button variant="ghost" size="sm" onClick={() => setSelectedColumn(null)}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                        <div className="p-4 overflow-y-auto flex-1 space-y-6">
                                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-md">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold uppercase mb-1">Selected Column</div>
                                                        <div className="font-medium text-blue-900 dark:text-blue-100 break-all">{selectedColumn.tableName}</div>
                                                        <div className="font-bold text-lg text-blue-900 dark:text-blue-100 break-all">.{selectedColumn.colName}</div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100/50 dark:hover:bg-blue-800/50 mt-1"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setPreviewTable(selectedColumn.tableName);
                                                            setPreviewOpen(true);
                                                        }}
                                                        title="Preview Table Data"
                                                    >
                                                        <Eye className="w-4 h-4 mr-1.5" />
                                                        Preview
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium flex items-center gap-2 text-slate-700 dark:text-slate-300">
                                                        Data Type
                                                    </label>
                                                    <select
                                                        className="w-full text-sm border border-gray-300 dark:border-slate-700 rounded-md p-2 bg-gray-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-950 focus:ring-2 focus:ring-blue-500 outline-none transition-colors"
                                                        value={selectedTables.find(t => t.name === selectedColumn.tableName)?.columns.find(c => c.name === selectedColumn.colName)?.data_type || 'text'}
                                                        onChange={(e) => {
                                                            const newTables = selectedTables.map(t => {
                                                                if (t.name !== selectedColumn.tableName) return t;
                                                                return {
                                                                    ...t,
                                                                    columns: t.columns.map(c => {
                                                                        if (c.name !== selectedColumn.colName) return c;
                                                                        return { ...c, data_type: e.target.value as any };
                                                                    })
                                                                };
                                                            });
                                                            setSelectedTables(newTables);
                                                        }}
                                                    >
                                                        <option value="text">Text</option>
                                                        <option value="number">Number</option>
                                                        <option value="date">Date</option>
                                                        <option value="boolean">Boolean</option>
                                                        <option value="json">JSON</option>
                                                    </select>
                                                </div>

                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium flex items-center gap-2 text-slate-700 dark:text-slate-300">
                                                        Format String
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="w-full text-sm border border-gray-300 dark:border-slate-700 rounded-md p-2 bg-gray-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-950 focus:ring-2 focus:ring-blue-500 outline-none transition-colors"
                                                        placeholder="e.g. $ #,##0.00"
                                                        value={selectedTables.find(t => t.name === selectedColumn.tableName)?.columns.find(c => c.name === selectedColumn.colName)?.format || ''}
                                                        onChange={(e) => {
                                                            const newTables = selectedTables.map(t => {
                                                                if (t.name !== selectedColumn.tableName) return t;
                                                                return {
                                                                    ...t,
                                                                    columns: t.columns.map(c => {
                                                                        if (c.name !== selectedColumn.colName) return c;
                                                                        return { ...c, format: e.target.value };
                                                                    })
                                                                };
                                                            });
                                                            setSelectedTables(newTables);
                                                        }}
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium flex items-center gap-2 text-slate-700 dark:text-slate-300">
                                                        <Wand2 className="w-4 h-4 text-purple-500" />
                                                        Synonyms (for AI)
                                                    </label>
                                                    <p className="text-xs text-gray-500 dark:text-slate-400">
                                                        Help the AI understand your data. Add aliases like "Revenue", "Income", "Total Sold".
                                                    </p>
                                                    <textarea
                                                        className="w-full text-sm border border-gray-300 dark:border-slate-700 rounded-md p-3 h-24 focus:ring-2 focus:ring-purple-500 outline-none resize-none bg-gray-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-950 transition-colors"
                                                        placeholder="Enter synonyms separated by commas..."
                                                        value={selectedTables.find(t => t.name === selectedColumn.tableName)?.columns.find(c => c.name === selectedColumn.colName)?.synonyms || ''}
                                                        onChange={(e) => {
                                                            const newTables = selectedTables.map(t => {
                                                                if (t.name !== selectedColumn.tableName) return t;
                                                                return {
                                                                    ...t,
                                                                    columns: t.columns.map(c => {
                                                                        if (c.name !== selectedColumn.colName) return c;
                                                                        return { ...c, synonyms: e.target.value };
                                                                    })
                                                                };
                                                            });
                                                            setSelectedTables(newTables);
                                                        }}
                                                    />
                                                    <p className="text-xs text-purple-600 italic">Separate multiple synonyms with commas.</p>
                                                </div>

                                                {/* PHI / Sensitive Data Toggle */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium flex items-center gap-2 text-slate-700 dark:text-slate-300">
                                                        <span className="text-red-500">🔒</span>
                                                        Protected Data
                                                    </label>
                                                    <p className="text-xs text-gray-500 dark:text-slate-400">
                                                        Mark this column as Protected Data. The hybrid agent will automatically sanitize it before upload.
                                                    </p>
                                                    <div
                                                        onClick={() => {
                                                            const currentCol = selectedTables
                                                                .find(t => t.name === selectedColumn.tableName)
                                                                ?.columns.find(c => c.name === selectedColumn.colName);
                                                            const newVal = !currentCol?.is_pd;
                                                            const newTables = selectedTables.map(t => {
                                                                if (t.name !== selectedColumn.tableName) return t;
                                                                return {
                                                                    ...t,
                                                                    columns: t.columns.map(c => {
                                                                        if (c.name !== selectedColumn.colName) return c;
                                                                        return { ...c, is_pd: newVal };
                                                                    })
                                                                };
                                                            });
                                                            setSelectedTables(newTables);
                                                        }}
                                                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all select-none ${
                                                            selectedTables
                                                                .find(t => t.name === selectedColumn.tableName)
                                                                ?.columns.find(c => c.name === selectedColumn.colName)
                                                                ?.is_pd
                                                                ? 'border-red-400 bg-red-50 dark:bg-red-950/30 dark:border-red-700'
                                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 bg-white dark:bg-slate-900'
                                                        }`}
                                                    >
                                                        <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors ${
                                                            selectedTables
                                                                .find(t => t.name === selectedColumn.tableName)
                                                                ?.columns.find(c => c.name === selectedColumn.colName)
                                                                ?.is_pd
                                                                ? 'bg-red-500'
                                                                : 'border-2 border-gray-300 dark:border-slate-600'
                                                        }`}>
                                                            {selectedTables
                                                                .find(t => t.name === selectedColumn.tableName)
                                                                ?.columns.find(c => c.name === selectedColumn.colName)
                                                                ?.is_pd && (
                                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className={`text-sm font-medium ${
                                                                selectedTables
                                                                    .find(t => t.name === selectedColumn.tableName)
                                                                    ?.columns.find(c => c.name === selectedColumn.colName)
                                                                    ?.is_pd
                                                                    ? 'text-red-700 dark:text-red-400'
                                                                    : 'text-slate-700 dark:text-slate-300'
                                                            }`}>
                                                                {selectedTables
                                                                    .find(t => t.name === selectedColumn.tableName)
                                                                    ?.columns.find(c => c.name === selectedColumn.colName)
                                                                    ?.is_pd
                                                                    ? 'Protected Data — will be sanitized by hybrid agent'
                                                                    : 'Not marked as Protected Data'}
                                                            </div>
                                                            <div className="text-xs text-gray-500 mt-0.5">Click to select/unselect</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* STEP 4: PROTECT DATA */}
                    {step === 'PROTECT' && (
                        <motion.div
                            key="protect"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="h-full"
                        >
                            <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-blue-50/80 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                                <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                                <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Map protected columns to data types and assign sanitization controls for the hybrid agent.</p>
                            </div>

                            {selectedTables.length === 0 ? (
                                <div className="text-center py-16 text-gray-400">
                                    <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                    <p>No tables selected. Go back and select tables first.</p>
                                </div>
                            ) : (() => {
                                const ALL_IDENTIFIERS: { key: string; label: string }[] = [
                                    { key: 'NAME', label: 'Names' }, { key: 'ADDRESS', label: 'Geographic Data' },
                                    { key: 'DOB', label: 'Dates (DOB, Admit)' }, { key: 'PHONE', label: 'Phone Numbers' },
                                    { key: 'FAX', label: 'Fax Numbers' }, { key: 'EMAIL', label: 'Email Addresses' },
                                    { key: 'SSN', label: 'Social Security #' }, { key: 'MRN', label: 'Medical Record #' },
                                    { key: 'BENEFICIARY', label: 'Beneficiary #' }, { key: 'ACCOUNT', label: 'Account Numbers' },
                                    { key: 'LICENSE', label: 'License / Cert #' }, { key: 'VEHICLE', label: 'Vehicle IDs' },
                                    { key: 'DEVICE', label: 'Device IDs' }, { key: 'URL', label: 'Web URLs' },
                                    { key: 'IP', label: 'IP Addresses' }, { key: 'BIOMETRIC', label: 'Biometric IDs' },
                                    { key: 'PHOTO', label: 'Photos' }, { key: 'UID', label: 'Unique IDs' },
                                    { key: 'AGE_OVER_89', label: 'Ages over 89' },
                                    ...customIdentifiers,
                                ];

                                const DEFAULT_ACTIONS: Record<string, string> = {
                                    NAME: 'mask', ADDRESS: 'mask', DOB: 'date_year_only', PHONE: 'mask',
                                    FAX: 'mask', EMAIL: 'mask', SSN: 'suppress', MRN: 'hash',
                                    BENEFICIARY: 'hash', ACCOUNT: 'hash', LICENSE: 'hash',
                                    VEHICLE: 'suppress', DEVICE: 'suppress', URL: 'suppress',
                                    IP: 'suppress', BIOMETRIC: 'suppress', PHOTO: 'suppress',
                                    UID: 'hash', AGE_OVER_89: 'date_age_range',
                                };

                                // Build reverse lookup: fqcol → { identifierKey, action }
                                const colMapping = new Map<string, { identifierKey: string; action: string }>();
                                Object.entries(pdApplierConfig).forEach(([idKey, conf]) => {
                                    (conf.columns || []).forEach((c: string) => {
                                        colMapping.set(c, { identifierKey: idKey, action: conf.action || 'mask' });
                                    });
                                });

                                // Filter only tables that have is_pd columns
                                const pdTables = selectedTables.filter(t =>
                                    t.columns?.some((c: any) => c.is_pd)
                                );

                                const totalPdCols = pdTables.reduce((s, t) => s + (t.columns?.filter((c: any) => c.is_pd)?.length || 0), 0);
                                // Only count columns that are still marked is_pd — avoids stale mapping count
                                const validPdColSet = new Set<string>();
                                pdTables.forEach(t => (t.columns as any[]).filter(c => c.is_pd).forEach(c => validPdColSet.add(`${t.name}.${c.name}`)));
                                const totalMapped = Object.values(pdApplierConfig).reduce(
                                    (s, v) => s + ((v.columns || []).filter((c: string) => validPdColSet.has(c)).length), 0
                                );

                                const setColumnMapping = (fqcol: string, identifierKey: string, action: string) => {
                                    setPdApplierConfig(prev => {
                                        const cleaned: any = {};
                                        Object.entries(prev).forEach(([key, conf]) => {
                                            const filteredCols = (conf.columns || []).filter((c: string) => c !== fqcol);
                                            if (filteredCols.length > 0) {
                                                cleaned[key] = { ...conf, columns: filteredCols };
                                            }
                                        });
                                        if (identifierKey) {
                                            const existing = cleaned[identifierKey] || { columns: [], action: DEFAULT_ACTIONS[identifierKey] || 'mask' };
                                            cleaned[identifierKey] = {
                                                ...existing,
                                                action,
                                                columns: [...(existing.columns || []), fqcol],
                                            };
                                        }
                                        return cleaned;
                                    });
                                };

                                return (
                                    <div className="border rounded-xl bg-white dark:bg-slate-900 overflow-hidden flex flex-col h-[calc(100vh-280px)] min-h-[400px]">
                                        {/* Header bar */}
                                        <div className="px-5 py-3.5 border-b bg-gradient-to-r from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 flex items-center justify-between gap-4">
                                            {/* Left: title */}
                                            <div className="shrink-0">
                                                <h3 className="font-semibold text-sm text-gray-900 dark:text-slate-100">Protected Data Columns</h3>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    Assign a protected data type and control for each protected column
                                                </p>
                                            </div>

                                            {/* Right: agent selector + badges */}
                                            <div className="flex items-center gap-3 ml-auto flex-wrap justify-end">
                                                {/* Hybrid Agent selector */}
                                                <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border transition-colors ${
                                                    !selectedAgentId
                                                        ? 'border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700'
                                                        : 'border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
                                                }`}>
                                                    <Shield className={`w-3.5 h-3.5 shrink-0 ${
                                                        !selectedAgentId ? 'text-red-500' : 'text-blue-500'
                                                    }`} />
                                                    <span className={`text-xs font-semibold whitespace-nowrap ${
                                                        !selectedAgentId ? 'text-red-600 dark:text-red-400' : 'text-blue-700 dark:text-blue-300'
                                                    }`}>Hybrid Agent:</span>
                                                    <select
                                                        value={selectedAgentId}
                                                        onChange={(e) => setSelectedAgentId(e.target.value)}
                                                        className={`text-xs px-2 py-1 border-0 rounded bg-transparent outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px] font-medium cursor-pointer ${
                                                            !selectedAgentId
                                                                ? 'text-red-600 dark:text-red-400'
                                                                : 'text-blue-800 dark:text-blue-200'
                                                        }`}
                                                    >
                                                        <option value="">— Select hybrid agent —</option>
                                                        {relayAgents.filter(a => a.status === 'ACTIVE').map(a => (
                                                            <option key={a.id} value={a.id}>✓ {a.name}</option>
                                                        ))}
                                                        {relayAgents.filter(a => a.status !== 'ACTIVE').map(a => (
                                                            <option key={a.id} value={a.id}>{a.name} ⚠ ({a.status})</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* PD columns badge */}
                                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                                    <Shield className="w-3.5 h-3.5 text-amber-600" />
                                                    <span className="text-xs font-bold text-amber-700 dark:text-amber-300">{totalPdCols}</span>
                                                    <span className="text-xs text-amber-600 dark:text-amber-400">Protected</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                                    <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                                                    <span className="text-xs font-bold text-blue-700 dark:text-blue-300">{totalMapped}</span>
                                                    <span className="text-xs text-blue-600 dark:text-blue-400">mapped</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Strategy legend — top */}
                                        <div className="px-5 py-2 border-b bg-white dark:bg-slate-900/50 flex items-center justify-between">
                                            <div className="flex items-center gap-5 text-[11px]">
                                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /><b className="text-gray-700 dark:text-slate-300">Mask</b><span className="text-gray-400">J***h</span></span>
                                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-500 inline-block" /><b className="text-gray-700 dark:text-slate-300">Encryption</b><span className="text-gray-400">encrypted</span></span>
                                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /><b className="text-gray-700 dark:text-slate-300">Suppress</b><span className="text-gray-400">removed</span></span>
                                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-300 dark:bg-slate-600 inline-block" /><b className="text-gray-700 dark:text-slate-300">Pass Through</b><span className="text-gray-400">unchanged</span></span>
                                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-400 inline-block" /><b className="text-gray-700 dark:text-slate-300">Year Only</b><span className="text-gray-400">1990</span></span>
                                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-400 inline-block" /><b className="text-gray-700 dark:text-slate-300">Age Range</b><span className="text-gray-400">30–34</span></span>
                                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-400 inline-block" /><b className="text-gray-700 dark:text-slate-300">Quarter</b><span className="text-gray-400">Q1 1990</span></span>
                                            </div>
                                            <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 px-3 py-1 rounded-full">Select a Protected Data Type first, then choose a Control</span>
                                        </div>

                                        {pdTables.length === 0 ? (
                                            <div className="flex-1 flex items-center justify-center text-gray-400">
                                                <div className="text-center">
                                                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                                    <p className="text-sm font-medium">No columns flagged as Protected Data</p>
                                                    <p className="text-xs mt-1 text-gray-400">Go to Step 2 and click the 🛡️ icon on columns to mark them as PD</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex-1 overflow-y-auto">
                                                {pdTables.map((table, tIdx) => {
                                                    const pdCols = table.columns.filter((c: any) => c.is_pd);
                                                    const isCollapsed = collapsedPdTables.has(table.name);
                                                    const toggleCollapse = () => {
                                                        setCollapsedPdTables(prev => {
                                                            const next = new Set(prev);
                                                            next.has(table.name) ? next.delete(table.name) : next.add(table.name);
                                                            return next;
                                                        });
                                                    };
                                                    const mappedInTable = pdCols.filter((c: any) => colMapping.has(`${table.name}.${c.name}`)).length;

                                                    return (
                                                        <div key={table.name} className={tIdx > 0 ? 'border-t border-gray-200 dark:border-slate-700' : ''}>
                                                            {/* Table header */}
                                                            <button
                                                                type="button"
                                                                onClick={toggleCollapse}
                                                                className="w-full px-5 py-3 flex items-center gap-3 bg-gray-50/80 dark:bg-slate-800/50 hover:bg-gray-100/80 dark:hover:bg-slate-800 transition-colors"
                                                            >
                                                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                                                                <Table className="w-4 h-4 text-blue-500" />
                                                                <span className="text-sm font-semibold text-gray-800 dark:text-slate-200 flex-1 text-left">{table.name}</span>
                                                                <span className="text-[11px] text-gray-400 mr-2">{table.columns.length} columns</span>
                                                                <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[11px] font-bold px-2 py-0.5 rounded-full">
                                                                    {pdCols.length} Protected
                                                                </span>
                                                                {mappedInTable > 0 && (
                                                                    <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[11px] font-bold px-2 py-0.5 rounded-full">
                                                                        {mappedInTable} mapped
                                                                    </span>
                                                                )}
                                                            </button>

                                                            {!isCollapsed && (
                                                                <>
                                                                    {/* Column table */}
                                                                    <table className="w-full">
                                                                        <thead>
                                                                            <tr className="bg-gray-50 dark:bg-slate-800/60">
                                                                                <th className="text-left px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Column Name</th>
                                                                                <th className="text-left px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={{ width: 200 }}>Protected Data Type</th>
                                                                                <th className="text-left px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={{ width: 160 }}>Control</th>
                                                                                <th className="text-left px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest" style={{ width: 170 }}>Control Policy</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                                                                            {pdCols.map((col: any) => {
                                                                                const fqcol = `${table.name}.${col.name}`;
                                                                                const mapping = colMapping.get(fqcol);
                                                                                const currentIdKey = mapping?.identifierKey || '';
                                                                                const currentAction = mapping?.action || DEFAULT_ACTIONS[currentIdKey] || 'mask';
                                                                                const isMapped = !!currentIdKey;
                                                                                return (
                                                                                    <tr key={fqcol} className={`transition-colors ${isMapped ? 'bg-blue-50/40 dark:bg-blue-900/5' : 'hover:bg-gray-50/50 dark:hover:bg-slate-800/30'}`}>
                                                                                        <td className="px-5 py-2.5">
                                                                                            <div className="flex items-center gap-2">
                                                                                                <Shield className={`w-3.5 h-3.5 shrink-0 ${isMapped ? 'text-blue-500' : 'text-gray-300 dark:text-slate-600'}`} />
                                                                                                <span className={`text-sm ${isMapped ? 'font-semibold text-gray-900 dark:text-slate-100' : 'text-gray-700 dark:text-slate-300'}`}>
                                                                                                    {col.name}
                                                                                                </span>
                                                                                                <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{col.data_type}</span>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-5 py-2.5">
                                                                                            <select
                                                                                                value={currentIdKey}
                                                                                                onChange={(e) => {
                                                                                                    const newKey = e.target.value;
                                                                                                    const action = DEFAULT_ACTIONS[newKey] || 'mask';
                                                                                                    setColumnMapping(fqcol, newKey, action);
                                                                                                }}
                                                                                                className={`w-full text-xs px-2.5 py-1.5 border rounded-md font-medium transition-all ${
                                                                                                    currentIdKey
                                                                                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                                                                                                        : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-400'
                                                                                                }`}
                                                                                            >
                                                                                                <option value="">— Select category —</option>
                                                                                                {ALL_IDENTIFIERS.map(id => (
                                                                                                    <option key={id.key} value={id.key}>{id.label}</option>
                                                                                                ))}
                                                                                            </select>
                                                                                        </td>
                                                                                        <td className="px-5 py-2.5">
                                                                                            {(() => {
                                                                                                const DATE_KEYS = new Set(['DOB', 'AGE_OVER_89']);
                                                                                                const isDateId = DATE_KEYS.has(currentIdKey) || col.data_type === 'date';
                                                                                                const actionColorClass = !currentIdKey
                                                                                                    ? 'opacity-30 cursor-not-allowed bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700'
                                                                                                    : currentAction === 'mask' ? 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300'
                                                                                                    : currentAction === 'hash' ? 'bg-sky-50 border-sky-200 text-sky-700 dark:bg-sky-900/20 dark:border-sky-800 dark:text-sky-300'
                                                                                                    : currentAction === 'suppress' ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                                                                                                    : currentAction.startsWith('date_') ? 'bg-violet-50 border-violet-200 text-violet-700 dark:bg-violet-900/20 dark:border-violet-800 dark:text-violet-300'
                                                                                                    : 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300';
                                                                                                return (
                                                                                                    <select
                                                                                                        value={currentAction}
                                                                                                        onChange={(e) => {
                                                                                                            if (currentIdKey) {
                                                                                                                setColumnMapping(fqcol, currentIdKey, e.target.value);
                                                                                                            }
                                                                                                        }}
                                                                                                        disabled={!currentIdKey}
                                                                                                        className={`w-full text-xs px-2.5 py-1.5 border rounded-md font-semibold transition-all ${actionColorClass}`}
                                                                                                    >
                                                                                                        {isDateId ? (
                                                                                                            <>
                                                                                                                <option value="date_year_only">Year Only  →  1990</option>
                                                                                                                <option value="date_month_only">Month Only  →  March</option>
                                                                                                                <option value="date_year_month">Year + Month  →  1990-03</option>
                                                                                                                <option value="date_quarter">Quarter  →  Q1 1990</option>
                                                                                                                <option value="date_age_range">Age Range (5-yr)  →  30–34</option>
                                                                                                                <option value="date_shift">Date Shift (±30 days)</option>
                                                                                                                <option value="suppress">Suppress (null)</option>
                                                                                                                <option value="passthrough">Pass Through</option>
                                                                                                            </>
                                                                                                        ) : (
                                                                                                            <>
                                                                                                                <option value="mask">Mask</option>
                                                                                                                <option value="hash">Encryption</option>
                                                                                                                <option value="suppress">Suppress</option>
                                                                                                                <option value="passthrough">Pass Through</option>
                                                                                                            </>
                                                                                                        )}
                                                                                                    </select>
                                                                                                );
                                                                                            })()}
                                                                                        </td>
                                                                                        <td className="px-5 py-2.5">
                                                                                            {currentAction === 'mask' && currentIdKey ? (
                                                                                                <select
                                                                                                    value={(maskOptions[fqcol] || {}).mask_char || '*'}
                                                                                                    onChange={(e) => {
                                                                                                        const val = e.target.value;
                                                                                                        const mode = ['partial_start','partial_end','format_preserve'].includes(val) ? val : 'full';
                                                                                                        setMaskOptions(prev => ({ ...prev, [fqcol]: { mask_char: val, mask_mode: mode } }));
                                                                                                    }}
                                                                                                    className="w-full text-xs px-2.5 py-1.5 border border-amber-200 rounded-md bg-amber-50/60 text-amber-800 dark:bg-amber-900/10 dark:border-amber-700 dark:text-amber-300 font-medium"
                                                                                                >
                                                                                                    <option value="*">* Asterisk  →  J***h</option>
                                                                                                    <option value="#"># Hash symbol  →  J###h</option>
                                                                                                    <option value="X">X Letter  →  JXXXh</option>
                                                                                                    <option value="•">• Dot  →  J•••h</option>
                                                                                                    <option value="partial_start">Partial (keep start)  →  Jo***</option>
                                                                                                    <option value="partial_end">Partial (keep end)  →  ***hn</option>
                                                                                                    <option value="[REDACTED]">[REDACTED] label</option>
                                                                                                    <option value="format_preserve">Format-preserving</option>
                                                                                                </select>
                                                                                            ) : (
                                                                                                <span className="text-[10px] text-gray-300 dark:text-slate-600 italic pl-1">
                                                                                                    {currentAction === 'hash' ? 'AES-256 encrypted' : currentAction.startsWith('date_') ? 'date transform' : '—'}
                                                                                                </span>
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}


                                        {/* Unmapped warning banner — shown below legend when any PD column is unconfigured */}
                                        {(() => {
                                            const unmappedCount = pdTables.reduce((sum, t) => {
                                                return sum + t.columns.filter((c: any) => c.is_pd && !colMapping.has(`${t.name}.${c.name}`)).length;
                                            }, 0);
                                            return unmappedCount > 0 ? (
                                                <div className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800">
                                                    <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                                    <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                                                        {unmappedCount} column{unmappedCount > 1 ? 's' : ''} still need{unmappedCount === 1 ? 's' : ''} a Protected Data Type and Control assigned before you can continue.
                                                    </span>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                );
                            })()}
                        </motion.div>
                    )}


{/* STEP 5: REVIEW */}
                    {step === 'REVIEW' && (() => {
                        const totalPdCount = selectedTables.reduce((sum: number, t: any) => sum + (t.columns?.filter((c: any) => c.is_pd)?.length || 0), 0);
                        return (
                        <motion.div
                            key="review"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="max-w-5xl mx-auto space-y-6 flex flex-col w-full"
                        >
                            <div className="mb-2">
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">Review & Settings</h3>
                                <p className="text-sm text-gray-500 dark:text-slate-400">Configure your database settings and review your model structure</p>
                            </div>

                            {/* Model Configuration Card */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm p-6 space-y-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
                                        <SlidersHorizontal size={20} />
                                    </div>
                                    <h4 className="text-base font-bold text-gray-900 dark:text-slate-100">Model Configuration</h4>
                                </div>
                                
                                <div className="flex flex-col gap-6">
                                    {/* Database Name */}
                                    <div className="space-y-2">
                                        <Label htmlFor="review-name" className="text-sm font-semibold text-gray-700 dark:text-slate-300">Database Model Name</Label>
                                        <Input id="review-name" {...register('name')} placeholder="e.g., Sales Data 2024" className="border-gray-200 dark:border-slate-700 shadow-sm rounded-lg text-lg py-6" />
                                        {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
                                        <p className="text-[12px] text-gray-500 dark:text-slate-400 mt-2">A unique name to identify this data model.</p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Domain Selection */}
                                        <div className="space-y-2">
                                            <Label htmlFor="domain-id" className="text-sm font-semibold text-gray-700 dark:text-slate-300">Target Domain (Optional)</Label>
                                            <Select value={watch('domain_id')} onValueChange={(val) => setValue('domain_id', val)}>
                                                <SelectTrigger id="domain-id" className="bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-700 shadow-sm focus:ring-2 focus:ring-blue-500 rounded-lg">
                                                    <SelectValue placeholder="Select a domain..." />
                                                </SelectTrigger>
                                                <SelectContent className="dark:bg-slate-900 dark:border-slate-800 max-w-md overflow-hidden rounded-lg">
                                                    {domains.length === 0 && (
                                                        <div className="p-4 text-center text-sm text-gray-500 italic">No domains found. Please create one in Knowledge base.</div>
                                                    )}
                                                    {domains.map((d) => (
                                                        <SelectItem key={d.id} value={d.id} className="dark:focus:bg-blue-900/40">
                                                            <div className="flex flex-col text-left py-1 w-full min-w-0">
                                                                <span className="font-semibold truncate block w-full">{d.name}</span>
                                                                {d.description && <span className="text-[11px] text-gray-500 dark:text-slate-400 truncate block w-full mt-0.5">{d.description}</span>}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.domain_id && <p className="text-sm font-medium text-red-500 animate-pulse">{errors.domain_id.message}</p>}
                                            <p className="text-[12px] text-gray-500 dark:text-slate-400 mt-2">Mapping a domain allows AI to understand your data context correctly.</p>
                                        </div>

                                        {/* Description */}
                                        <div className="space-y-2">
                                            <Label htmlFor="review-description" className="text-sm font-semibold text-gray-700 dark:text-slate-300">Description (Optional)</Label>
                                            <Textarea id="review-description" {...register('description')} placeholder="Brief description of this data model" className="resize-none border-gray-200 dark:border-slate-700 shadow-sm rounded-lg h-[42px]" />
                                            <p className="text-[12px] text-gray-500 dark:text-slate-400 mt-2">Add a short description to help others understand this model.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Storage Mode Card */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm p-6 space-y-6">
                                <h4 className="text-base font-bold text-gray-900 dark:text-slate-100">Storage Mode</h4>
                                
                                {(() => {
                                    const hasPdColumns = selectedTables.some(t => t.columns?.some((c: any) => c.is_pd));
                                    if (hasPdColumns && watch('storage_mode') !== 'import') {
                                        setValue('storage_mode', 'import');
                                    }
                                    return hasPdColumns ? (
                                        <div className="flex items-start gap-3 p-4 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl">
                                            <Shield className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                                            <p className="text-sm text-amber-700 dark:text-amber-400 font-medium leading-relaxed">
                                                <strong>DirectQuery disabled.</strong> One or more columns are marked as Protected Data. Cached mode is required so the Hybrid Agent can sanitize data before it reaches the platform.
                                            </p>
                                        </div>
                                    ) : null;
                                })()}
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* DirectQuery */}
                                    <div
                                        className={`p-5 border-2 rounded-xl transition-all relative flex flex-col justify-center ${
                                            selectedTables.some(t => t.columns?.some((c: any) => c.is_pd))
                                                ? 'border-gray-100 bg-white dark:bg-slate-900 opacity-60 cursor-not-allowed'
                                                : watch('storage_mode') === 'direct_query'
                                                    ? 'border-indigo-600 bg-indigo-50/30 dark:bg-indigo-900/20 cursor-pointer'
                                                    : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 bg-white dark:bg-slate-900 cursor-pointer'
                                        }`}
                                        onClick={() => {
                                            if (!selectedTables.some(t => t.columns?.some((c: any) => c.is_pd))) {
                                                setValue('storage_mode', 'direct_query');
                                            }
                                        }}
                                        title={selectedTables.some(t => t.columns?.some((c: any) => c.is_pd)) ? 'DirectQuery is disabled when Protected Data columns are marked' : undefined}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="font-bold text-gray-900 dark:text-slate-100">DirectQuery</div>
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${watch('storage_mode') === 'direct_query' ? 'border-indigo-600' : 'border-gray-300 dark:border-slate-600'}`}>
                                                {watch('storage_mode') === 'direct_query' && <div className="w-2.5 h-2.5 rounded-full bg-indigo-600"></div>}
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-slate-400 mb-4">Live connection. Real-time data.</div>
                                        {selectedTables.some(t => t.columns?.some((c: any) => c.is_pd)) && (
                                            <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium mt-auto"><AlertCircle className="w-3.5 h-3.5" /> Unavailable with PD columns</div>
                                        )}
                                    </div>
                                    
                                    {/* Cached */}
                                    <div
                                        className={`p-5 border-2 rounded-xl cursor-pointer transition-all relative flex flex-col justify-center ${
                                            watch('storage_mode') === 'import'
                                                ? 'border-indigo-600 bg-indigo-50/10 dark:bg-indigo-900/20 shadow-sm'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 bg-white dark:bg-slate-900'
                                        }`}
                                        onClick={() => setValue('storage_mode', 'import')}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className={`font-bold ${watch('storage_mode') === 'import' ? 'text-indigo-800 dark:text-indigo-300' : 'text-gray-900 dark:text-slate-100'}`}>Cached</div>
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${watch('storage_mode') === 'import' ? 'border-indigo-600' : 'border-gray-300 dark:border-slate-600'}`}>
                                                {watch('storage_mode') === 'import' && <div className="w-2.5 h-2.5 rounded-full bg-indigo-600"></div>}
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-slate-400 mb-4">High performance. Requires refresh.</div>
                                        {selectedTables.some(t => t.columns?.some((c: any) => c.is_pd)) && (
                                            <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium mt-auto"><CheckCircle className="w-3.5 h-3.5" /> Required for Protected Data sanitization</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Model Summary Card */}
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h4 className="text-base font-bold text-gray-900 dark:text-slate-100">Model Summary</h4>
                                    <p className="text-xs text-gray-400">Click any card to view details</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {/* Tables */}
                                    <button type="button" onClick={() => setReviewModal(reviewModal === 'tables' ? null : 'tables')} className={`group flex items-center p-4 bg-white dark:bg-slate-900 rounded-xl border transition-all text-left ${reviewModal === 'tables' ? 'border-indigo-500 shadow-md ring-1 ring-indigo-500' : 'border-gray-200 dark:border-slate-700 hover:border-indigo-200 hover:shadow-md'}`}>
                                        <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center mr-4 group-hover:bg-indigo-100 transition-colors shrink-0">
                                            <Table className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100 leading-tight">{selectedTables.length}</div>
                                            <div className="text-xs text-gray-500 font-medium mt-0.5">Tables</div>
                                        </div>
                                        <ChevronRight className={`w-4 h-4 transition-transform ${reviewModal === 'tables' ? 'rotate-90 text-indigo-500' : 'text-gray-300 group-hover:text-indigo-400'}`} />
                                    </button>
                                    
                                    {/* Measures */}
                                    <button type="button" onClick={() => setReviewModal(reviewModal === 'measures' ? null : 'measures')} className={`group flex items-center p-4 bg-white dark:bg-slate-900 rounded-xl border transition-all text-left ${reviewModal === 'measures' ? 'border-emerald-500 shadow-md ring-1 ring-emerald-500' : 'border-gray-200 dark:border-slate-700 hover:border-emerald-200 hover:shadow-md'}`}>
                                        <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mr-4 group-hover:bg-emerald-100 transition-colors shrink-0">
                                            <Calculator className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100 leading-tight">{measures.length}</div>
                                            <div className="text-xs text-gray-500 font-medium mt-0.5">Measures</div>
                                        </div>
                                        <ChevronRight className={`w-4 h-4 transition-transform ${reviewModal === 'measures' ? 'rotate-90 text-emerald-500' : 'text-gray-300 group-hover:text-emerald-400'}`} />
                                    </button>
                                    
                                    {/* Relationships */}
                                    <button type="button" onClick={() => setReviewModal(reviewModal === 'relationships' ? null : 'relationships')} className={`group flex items-center p-4 bg-white dark:bg-slate-900 rounded-xl border transition-all text-left ${reviewModal === 'relationships' ? 'border-purple-500 shadow-md ring-1 ring-purple-500' : 'border-gray-200 dark:border-slate-700 hover:border-purple-200 hover:shadow-md'}`}>
                                        <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center mr-4 group-hover:bg-purple-100 transition-colors shrink-0">
                                            <Database className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100 leading-tight">{relationships.length}</div>
                                            <div className="text-xs text-gray-500 font-medium mt-0.5">Relationships</div>
                                        </div>
                                        <ChevronRight className={`w-4 h-4 transition-transform ${reviewModal === 'relationships' ? 'rotate-90 text-purple-500' : 'text-gray-300 group-hover:text-purple-400'}`} />
                                    </button>
                                    
                                    {/* Protected */}
                                    <button type="button" onClick={() => setReviewModal(reviewModal === 'protected' ? null : 'protected')} className={`group flex items-center p-4 bg-white dark:bg-slate-900 rounded-xl border transition-all text-left ${reviewModal === 'protected' ? 'border-amber-500 shadow-md ring-1 ring-amber-500' : 'border-gray-200 dark:border-slate-700 hover:border-amber-200 hover:shadow-md'}`}>
                                        <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center mr-4 group-hover:bg-amber-100 transition-colors shrink-0">
                                            <Shield className="w-6 h-6 text-amber-500 dark:text-amber-400" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100 leading-tight">{totalPdCount}</div>
                                            <div className="text-xs text-gray-500 font-medium mt-0.5">Protected</div>
                                        </div>
                                        <ChevronRight className={`w-4 h-4 transition-transform ${reviewModal === 'protected' ? 'rotate-90 text-amber-500' : 'text-gray-300 group-hover:text-amber-400'}`} />
                                    </button>
                                </div>
                            </div>

                                {/* Inline Detail View */}
                                <AnimatePresence mode="wait">
                                    {reviewModal && (
                                        <motion.div
                                            key={reviewModal}
                                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                            animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="p-5 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-xl">
                                                <div className="flex items-center justify-between mb-4">
                                                    <h5 className="font-semibold flex items-center gap-2 text-gray-900 dark:text-slate-100">
                                                        {reviewModal === 'tables' && <><Table className="w-5 h-5 text-indigo-500" /> Tables ({selectedTables.length})</>}
                                                        {reviewModal === 'measures' && <><Calculator className="w-5 h-5 text-emerald-500" /> Measures ({measures.length})</>}
                                                        {reviewModal === 'relationships' && <><Database className="w-5 h-5 text-purple-500" /> Relationships ({relationships.length})</>}
                                                        {reviewModal === 'protected' && <><Shield className="w-5 h-5 text-amber-500" /> Protected Columns ({totalPdCount})</>}
                                                    </h5>
                                                    <button onClick={() => setReviewModal(null)} className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                
                                                <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar space-y-2">
                                                    {/* Tables */}
                                                    {reviewModal === 'tables' && (
                                                        <div className="space-y-2 pb-2">
                                                            {selectedTables.map(t => {
                                                                const isExpanded = expandedReviewTables.includes(t.name);
                                                                return (
                                                                <div key={t.name} className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                                                    <div 
                                                                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                                                                        onClick={() => {
                                                                            setExpandedReviewTables(prev => 
                                                                                prev.includes(t.name) ? prev.filter(n => n !== t.name) : [...prev, t.name]
                                                                            );
                                                                        }}
                                                                    >
                                                                        <div className="flex items-center gap-2.5">
                                                                            <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                                            <Table className="w-4 h-4 text-indigo-400 shrink-0" />
                                                                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{t.name}</span>
                                                                        </div>
                                                                        <span className="text-xs text-gray-500 bg-gray-50 dark:bg-slate-800 px-2.5 py-1 rounded-full border border-gray-100 dark:border-slate-700">{t.columns.length} columns</span>
                                                                    </div>
                                                                    
                                                                    <AnimatePresence>
                                                                        {isExpanded && (
                                                                            <motion.div
                                                                                initial={{ height: 0, opacity: 0 }}
                                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                                exit={{ height: 0, opacity: 0 }}
                                                                            >
                                                                                <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-slate-800">
                                                                                    <div className="max-h-60 overflow-y-auto custom-scrollbar pr-2 space-y-1">
                                                                                        {t.columns.map((c: any) => (
                                                                                            <div key={c.name} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded">
                                                                                                <div className="flex items-center gap-2">
                                                                                                    {c.is_pd && <Shield className="w-3 h-3 text-amber-500" />}
                                                                                                    <span className="text-xs font-mono text-gray-600 dark:text-slate-300">{c.name}</span>
                                                                                                </div>
                                                                                                <span className="text-[10px] text-gray-400 font-mono bg-gray-50 dark:bg-slate-800 px-1.5 rounded">{c.data_type}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            </motion.div>
                                                                        )}
                                                                    </AnimatePresence>
                                                                </div>
                                                            )})}
                                                        </div>
                                                    )}
                                                    {/* Measures */}
                                                    {reviewModal === 'measures' && (
                                                        <div className="space-y-2 pb-2">
                                                            {measures.length === 0 ? (
                                                                <div className="py-10 text-center text-sm text-gray-400 italic"><Calculator className="w-10 h-10 mx-auto mb-3 opacity-20" />No measures defined</div>
                                                            ) : measures.map((m, i) => (
                                                                <div key={i} className="px-4 py-3 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm">
                                                                    <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{m.name}</span>
                                                                    <p className="text-xs text-gray-500 font-mono mt-1.5 bg-gray-50 dark:bg-slate-800 px-2.5 py-1.5 rounded border border-gray-100 dark:border-slate-700">{m.expression}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {/* Relationships */}
                                                    {reviewModal === 'relationships' && (
                                                        <div className="space-y-3 pb-2">
                                                            {relationships.length === 0 ? (
                                                                <div className="py-10 text-center text-sm text-gray-400 italic"><Database className="w-10 h-10 mx-auto mb-3 opacity-20" />No relationships defined</div>
                                                            ) : relationships.map((rel, idx) => (
                                                                <div key={idx} className="flex items-center gap-6 p-4 bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-900/50 rounded-xl shadow-sm">
                                                                    {/* Checkmark icon */}
                                                                    <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center shrink-0">
                                                                        <Check className="w-3.5 h-3.5 text-white" />
                                                                    </div>
                                                                    
                                                                    <div className="flex-1 flex items-center">
                                                                        {/* Left side (From) */}
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="text-sm font-bold text-gray-700 dark:text-slate-300 truncate">{rel.from_table}</div>
                                                                            <div className="text-[11px] text-gray-500 font-mono mt-0.5 truncate">{rel.from_column}</div>
                                                                        </div>
                                                                        
                                                                        {/* Middle (Relationship Line) */}
                                                                        <div className="px-4 flex flex-col items-center justify-center shrink-0 w-28">
                                                                            <div className="text-[11px] font-bold text-slate-400 mb-1 tracking-widest">
                                                                                {rel.cardinality === 'many_to_one' ? '* : 1' : rel.cardinality === 'one_to_many' ? '1 : *' : rel.cardinality === 'one_to_one' ? '1 : 1' : '* : *'}
                                                                            </div>
                                                                            <div className="w-full flex items-center justify-center relative">
                                                                                <div className="h-[2px] bg-slate-200 dark:bg-slate-700 w-full absolute top-1/2 -translate-y-1/2 rounded-full"></div>
                                                                                <div className="w-6 h-6 rounded-full bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 flex items-center justify-center relative z-10">
                                                                                    <ArrowLeftRight className="w-3 h-3 text-slate-400" />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Right side (To) */}
                                                                        <div className="flex-1 min-w-0 pl-4">
                                                                            <div className="text-sm font-bold text-gray-700 dark:text-slate-300 truncate">{rel.to_table}</div>
                                                                            <div className="text-[11px] text-gray-500 font-mono mt-0.5 truncate">{rel.to_column}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {/* Protected */}
                                                    {reviewModal === 'protected' && (
                                                        <div className="space-y-3 pb-2">
                                                            {totalPdCount === 0 ? (
                                                                <div className="py-10 text-center text-sm text-gray-400 italic"><Shield className="w-10 h-10 mx-auto mb-3 opacity-20" />No protected columns</div>
                                                            ) : selectedTables.filter(t => t.columns?.some((c: any) => c.is_pd)).map(t => (
                                                                <div key={t.name} className="bg-white dark:bg-slate-900 border border-amber-100 dark:border-amber-900/30 rounded-lg p-3 shadow-sm">
                                                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                                        <Table className="w-3.5 h-3.5" /> {t.name}
                                                                    </div>
                                                                    <div className="space-y-1.5">
                                                                        {t.columns.filter((c: any) => c.is_pd).map((c: any) => (
                                                                            <div key={`${t.name}.${c.name}`} className="flex items-center justify-between px-3 py-2 bg-amber-50/40 dark:bg-amber-900/10 rounded-md border border-amber-100/50 dark:border-amber-800/30">
                                                                                <div className="flex items-center gap-2.5"><Shield className="w-3.5 h-3.5 text-amber-500 shrink-0" /><span className="text-sm font-medium text-amber-900 dark:text-amber-200">{c.name}</span></div>
                                                                                <span className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-100/80 dark:bg-amber-900/40 px-2 py-0.5 rounded-full">{c.data_type}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                        </motion.div>
                        );
                    })()}
                </AnimatePresence>
            </div>

            {/* Footer Navigation */}
            <div className="h-20 border-t border-gray-200 dark:border-slate-800 bg-gray-50/80 dark:bg-slate-900/80 px-8 flex items-center justify-between shrink-0">
                <Button
                    variant="outline"
                    onClick={() => {
                        const prevIdx = Math.max(0, currentStepIndex - 1);
                        setStep(steps[prevIdx].key as any);
                    }}
                    disabled={currentStepIndex === 0}
                    className={`px-6 ${currentStepIndex === 0 ? 'opacity-0 pointer-events-none' : ''}`}
                >
                    <ChevronLeft className="mr-2 h-4 w-4" /> Back
                </Button>

                {currentStepIndex === steps.length - 1 ? (
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading}
                        className="bg-primary hover:opacity-90 transition-opacity text-white px-8 h-10"
                    >
                        {isLoading ? (
                            <><Loader2 className="animate-spin mr-2 h-4 w-4" /> Saving...</>
                        ) : (
                            <><Save className="mr-2 h-4 w-4" /> {dataModelId ? 'Update' : 'Create Data Model'}</>
                        )}
                    </Button>
                ) : (
                    <Button
                        onClick={() => {
                            if (step === 'CONNECT') {
                                handleIntrospect();
                                return;
                            }

                            if (step === 'SELECT' && selectedTables.length === 0) return;

                            const nextIdx = Math.min(steps.length - 1, currentStepIndex + 1);
                            setStep(steps[nextIdx].key as any);
                        }}
                        disabled={
                            (step === 'CONNECT' && !selectedDatasourceId) ||
                            (step === 'SELECT' && selectedTables.length === 0) ||
                            (step === 'CONNECT' && isIntrospecting) ||
                            (step === 'PROTECT' && (() => {
                                // Must select an agent when any PD columns exist
                                const pdTbls = selectedTables.filter(t => t.columns?.some((c: any) => c.is_pd));
                                if (pdTbls.length === 0) return false; // no PD columns — free to proceed
                                if (!selectedAgentId) return true;    // PD exists but no agent selected — BLOCK
                                // Agent selected (even offline/revoked) — allow
                                const mapped = Object.values(pdApplierConfig).reduce((s: number, v: any) => s + (v.columns?.length || 0), 0);
                                const total = pdTbls.reduce((s, t) => s + (t.columns?.filter((c: any) => c.is_pd)?.length || 0), 0);
                                return mapped < total; // all PD columns must be mapped too
                            })())
                        }
                        className="bg-primary hover:opacity-90 transition-opacity text-white px-8 h-10"
                    >
                        {step === 'CONNECT' && isIntrospecting ? (
                            <><Loader2 className="animate-spin mr-2 h-4 w-4" /> Connecting...</>
                        ) : (
                            <>Next <ChevronRight className="ml-2 h-4 w-4" /></>
                        )}
                    </Button>
                )}
            </div>

            {/* DATA PREVIEW MODAL */}
            {(selectedDatasourceId || getValues('datasource_id')) && (
                <DataPreviewModal
                    isOpen={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    dataSourceId={selectedDatasourceId || getValues('datasource_id')}
                    objectName={previewTable}
                />
            )}
            {/* Date Table Wizard Modal */}
            {effectiveModelId && (
                <DateTableWizard
                    isOpen={isDateWizardOpen}
                    onClose={() => setIsDateWizardOpen(false)}
                    onSuccess={() => {
                        loadDataModel(effectiveModelId);
                    }}
                    dataModelId={effectiveModelId}
                />
            )}
            {/* Relationship Edit Dialog */}
            <Dialog open={isRelationshipDialogOpen} onOpenChange={setIsRelationshipDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Relationship</DialogTitle>
                    </DialogHeader>

                    {editingRelationship && (
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="p-3 bg-gray-50 rounded border">
                                    <span className="text-gray-500 block text-xs">From</span>
                                    <span className="font-medium">{editingRelationship.from_table}.{editingRelationship.from_column}</span>
                                </div>
                                <div className="p-3 bg-gray-50 rounded border">
                                    <span className="text-gray-500 block text-xs">To</span>
                                    <span className="font-medium">{editingRelationship.to_table}.{editingRelationship.to_column}</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Cardinality</Label>
                                <select
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                    value={editingRelationship.cardinality}
                                    onChange={(e) => setEditingRelationship({ ...editingRelationship, cardinality: e.target.value as any })}
                                >
                                    <option value="many_to_one">Many to One (* : 1)</option>
                                    <option value="one_to_many">One to Many (1 : *)</option>
                                    <option value="one_to_one">One to One (1 : 1)</option>
                                    <option value="many_to_many">Many to Many (* : *)</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <Label>Cross Filtering Direction</Label>
                                <select
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                    value={editingRelationship.crossFilteringBehavior || 'Single'}
                                    onChange={(e) => setEditingRelationship({ ...editingRelationship, crossFilteringBehavior: e.target.value })}
                                >
                                    <option value="Single">Single</option>
                                    <option value="Both">Both</option>
                                </select>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="flex justify-between sm:justify-between">
                        <Button variant="destructive" onClick={handleDeleteRelationship}>Delete</Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setIsRelationshipDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleSaveRelationship}>Save</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog >

            <RelationshipReviewDialog
                isOpen={isAiReviewOpen}
                onClose={() => setIsAiReviewOpen(false)}
                suggestions={aiSuggestions}
                relationships={relationships} // Fix missing prop
                onApply={handleApplyAiSuggestions}
            />

            {/* Embedded DataSource Wizard Modal */}
            <DataSourceWizard 
                isOpen={isSourceWizardOpen}
                onClose={() => setIsSourceWizardOpen(false)}
                onSuccess={() => {
                    setIsSourceWizardOpen(false);
                    fetchDatasources();
                }}
            />

        </div >
    );
};
